import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase/config';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Package, Upload, X, DollarSign, Hash, AlignLeft, Tag, Truck, Award, Building2, Globe, ShoppingCart, Plus, Trash2, Settings, Layers } from 'lucide-react';

const ProductRegistration = ({ userInfo }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        brand: '',
        category: 'implant-fixture',
        subCategory: '',
        price: '',
        originalPrice: '',
        stock: '',
        unit: 'EA',
        minOrder: '1',
        description: '',
        features: '',
        specifications: '',
        manufacturer: '',
        origin: '대한민국',
        certification: '',
        shippingInfo: '무료배송',
        returnPolicy: '7일 이내 반품 가능',
        images: [],
        taxInvoice: false,
        hasOptions: false,
        options: [] // 상품 옵션 (예: 규격, 사이즈 등)
    });
    const [imagePreviews, setImagePreviews] = useState([]);
    const [activeTab, setActiveTab] = useState('basic');
    const [optionGroups, setOptionGroups] = useState([]); // 옵션 그룹 (예: 직경, 길이)

    useEffect(() => {
        if (!userInfo) {
            alert('로그인이 필요합니다.');
            window.location.href = '/login';
            return;
        }

        // 승인된 판매자만 접근 가능
        if (userInfo.sellerStatus !== 'approved') {
            alert('판매자 승인 후 상품 등록이 가능합니다.');
            window.location.href = '/seller-application';
            return;
        }
    }, [userInfo]);

    // 치과 재료상 전문 카테고리
    const categories = [
        {
            value: 'implant',
            label: '임플란트',
            subCategories: [
                { value: 'implant-fixture', label: '픽스처' },
                { value: 'implant-abutment', label: '어버트먼트' },
                { value: 'implant-kit', label: '수술 키트' },
                { value: 'implant-bone', label: '골이식재' },
                { value: 'implant-membrane', label: '차폐막' },
                { value: 'implant-accessory', label: '악세서리' },
            ]
        },
        {
            value: 'restorative',
            label: '수복재료',
            subCategories: [
                { value: 'restorative-composite', label: '컴포짓 레진' },
                { value: 'restorative-cement', label: '시멘트' },
                { value: 'restorative-adhesive', label: '접착제/본딩' },
                { value: 'restorative-temporary', label: '임시 수복재' },
            ]
        },
        {
            value: 'endodontic',
            label: '근관치료',
            subCategories: [
                { value: 'endo-file', label: '파일/리머' },
                { value: 'endo-sealer', label: '실러' },
                { value: 'endo-guttapercha', label: '거타퍼챠' },
                { value: 'endo-irrigant', label: '세척제' },
            ]
        },
        {
            value: 'preventive',
            label: '예방치료',
            subCategories: [
                { value: 'preventive-fluoride', label: '불소제제' },
                { value: 'preventive-sealant', label: '실란트' },
                { value: 'preventive-polish', label: '연마재' },
            ]
        },
        {
            value: 'impression',
            label: '인상재료',
            subCategories: [
                { value: 'impression-silicone', label: '실리콘 인상재' },
                { value: 'impression-alginate', label: '알지네이트' },
                { value: 'impression-tray', label: '트레이' },
            ]
        },
        {
            value: 'orthodontic',
            label: '교정재료',
            subCategories: [
                { value: 'ortho-bracket', label: '브라켓' },
                { value: 'ortho-wire', label: '와이어' },
                { value: 'ortho-elastic', label: '엘라스틱' },
                { value: 'ortho-accessory', label: '교정 악세서리' },
            ]
        },
        {
            value: 'surgical',
            label: '외과용품',
            subCategories: [
                { value: 'surgical-suture', label: '봉합사' },
                { value: 'surgical-blade', label: '블레이드' },
                { value: 'surgical-instrument', label: '수술기구' },
            ]
        },
        {
            value: 'hygiene',
            label: '위생용품',
            subCategories: [
                { value: 'hygiene-glove', label: '글러브' },
                { value: 'hygiene-mask', label: '마스크' },
                { value: 'hygiene-sterilization', label: '멸균/소독' },
                { value: 'hygiene-disposable', label: '일회용품' },
            ]
        },
        {
            value: 'equipment',
            label: '장비',
            subCategories: [
                { value: 'equip-handpiece', label: '핸드피스' },
                { value: 'equip-light', label: '광중합기' },
                { value: 'equip-scaler', label: '스케일러' },
                { value: 'equip-xray', label: 'X-ray 관련' },
                { value: 'equip-other', label: '기타 장비' },
            ]
        },
        {
            value: 'lab-material',
            label: '기공재료',
            subCategories: [
                { value: 'lab-zirconia', label: '지르코니아' },
                { value: 'lab-porcelain', label: '포세린' },
                { value: 'lab-wax', label: '왁스' },
                { value: 'lab-alloy', label: '합금' },
            ]
        },
        { value: 'other', label: '기타', subCategories: [] }
    ];

    const units = [
        { value: 'EA', label: '개' },
        { value: 'BOX', label: '박스' },
        { value: 'SET', label: '세트' },
        { value: 'PACK', label: '팩' },
        { value: 'KIT', label: '키트' },
        { value: 'BOTTLE', label: '병' },
        { value: 'TUBE', label: '튜브' },
        { value: 'SYRINGE', label: '시린지' },
        { value: 'KG', label: 'kg' },
        { value: 'G', label: 'g' },
        { value: 'ML', label: 'ml' },
        { value: 'L', label: 'L' }
    ];

    // 현재 선택된 카테고리의 서브카테고리 가져오기
    const getSubCategories = () => {
        const mainCategory = categories.find(cat => cat.value === formData.category);
        return mainCategory?.subCategories || [];
    };

    // 옵션 그룹 추가
    const addOptionGroup = () => {
        setOptionGroups([...optionGroups, {
            id: Date.now(),
            name: '',
            values: [{ id: Date.now(), value: '', additionalPrice: 0, stock: 0 }]
        }]);
        setFormData(prev => ({ ...prev, hasOptions: true }));
    };

    // 옵션 그룹 삭제
    const removeOptionGroup = (groupId) => {
        const newGroups = optionGroups.filter(g => g.id !== groupId);
        setOptionGroups(newGroups);
        if (newGroups.length === 0) {
            setFormData(prev => ({ ...prev, hasOptions: false }));
        }
    };

    // 옵션 그룹명 변경
    const updateOptionGroupName = (groupId, name) => {
        setOptionGroups(optionGroups.map(g =>
            g.id === groupId ? { ...g, name } : g
        ));
    };

    // 옵션 값 추가
    const addOptionValue = (groupId) => {
        setOptionGroups(optionGroups.map(g =>
            g.id === groupId ? {
                ...g,
                values: [...g.values, { id: Date.now(), value: '', additionalPrice: 0, stock: 0 }]
            } : g
        ));
    };

    // 옵션 값 삭제
    const removeOptionValue = (groupId, valueId) => {
        setOptionGroups(optionGroups.map(g =>
            g.id === groupId ? {
                ...g,
                values: g.values.filter(v => v.id !== valueId)
            } : g
        ));
    };

    // 옵션 값 업데이트
    const updateOptionValue = (groupId, valueId, field, newValue) => {
        setOptionGroups(optionGroups.map(g =>
            g.id === groupId ? {
                ...g,
                values: g.values.map(v =>
                    v.id === valueId ? { ...v, [field]: newValue } : v
                )
            } : g
        ));
    };

    // 옵션 조합 생성
    const generateOptionCombinations = () => {
        if (optionGroups.length === 0) return [];

        const combine = (groups, index = 0, current = {}) => {
            if (index === groups.length) return [current];
            const group = groups[index];
            const results = [];
            for (const value of group.values) {
                if (value.value) {
                    results.push(...combine(groups, index + 1, {
                        ...current,
                        [group.name]: {
                            value: value.value,
                            additionalPrice: parseInt(value.additionalPrice) || 0,
                            stock: parseInt(value.stock) || 0
                        }
                    }));
                }
            }
            return results;
        };

        return combine(optionGroups.filter(g => g.name && g.values.some(v => v.value)));
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        
        if (formData.images.length + files.length > 5) {
            alert('이미지는 최대 5개까지 등록 가능합니다.');
            return;
        }

        const validFiles = files.filter(file => {
            if (file.size > 5 * 1024 * 1024) {
                alert(`${file.name}은(는) 5MB를 초과합니다.`);
                return false;
            }
            if (!file.type.startsWith('image/')) {
                alert(`${file.name}은(는) 이미지 파일이 아닙니다.`);
                return false;
            }
            return true;
        });

        setFormData(prev => ({
            ...prev,
            images: [...prev.images, ...validFiles]
        }));

        // 미리보기 생성
        validFiles.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreviews(prev => [...prev, reader.result]);
            };
            reader.readAsDataURL(file);
        });
    };

    const removeImage = (index) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.price || !formData.stock) {
            alert('필수 항목을 모두 입력해주세요.');
            return;
        }

        if (parseInt(formData.minOrder) > parseInt(formData.stock)) {
            alert('최소 주문 수량은 재고 수량보다 클 수 없습니다.');
            return;
        }

        if (formData.images.length === 0) {
            alert('최소 1개의 상품 이미지를 등록해주세요.');
            return;
        }

        setLoading(true);

        try {
            // 이미지 업로드
            const imageUrls = [];
            for (const image of formData.images) {
                const imageRef = ref(storage, `products/${userInfo.uid}/${Date.now()}_${image.name}`);
                await uploadBytes(imageRef, image);
                const url = await getDownloadURL(imageRef);
                imageUrls.push(url);
            }

            // 상품 정보 저장
            await addDoc(collection(db, 'marketplaceProducts'), {
                sellerId: userInfo.uid,
                sellerName: userInfo.businessName || userInfo.storeName || userInfo.name || userInfo.email,
                sellerType: userInfo.businessType,
                companyId: userInfo.companyId || '',
                companyName: userInfo.companyName || userInfo.businessName || '',
                name: formData.name,
                brand: formData.brand,
                category: formData.category,
                subCategory: formData.subCategory,
                price: parseInt(formData.price),
                originalPrice: formData.originalPrice ? parseInt(formData.originalPrice) : null,
                discountRate: formData.originalPrice ? Math.round((1 - parseInt(formData.price) / parseInt(formData.originalPrice)) * 100) : 0,
                stock: parseInt(formData.stock),
                unit: formData.unit,
                minOrder: parseInt(formData.minOrder) || 1,
                description: formData.description,
                features: formData.features,
                specifications: formData.specifications,
                manufacturer: formData.manufacturer,
                origin: formData.origin,
                certification: formData.certification,
                shippingInfo: formData.shippingInfo,
                returnPolicy: formData.returnPolicy,
                taxInvoice: formData.taxInvoice || false,
                images: imageUrls,
                hasOptions: formData.hasOptions,
                optionGroups: optionGroups.filter(g => g.name && g.values.some(v => v.value)),
                options: generateOptionCombinations(),
                status: 'pending', // 관리자 승인 대기
                viewCount: 0,
                likeCount: 0,
                orderCount: 0,
                rating: 0,
                reviewCount: 0,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });

            alert('상품이 등록되었습니다!');
            
            // 폼 초기화
            setFormData({
                name: '',
                brand: '',
                category: 'implant-fixture',
                subCategory: '',
                price: '',
                originalPrice: '',
                stock: '',
                unit: 'EA',
                minOrder: '1',
                description: '',
                features: '',
                specifications: '',
                manufacturer: '',
                origin: '대한민국',
                certification: '',
                shippingInfo: '무료배송',
                returnPolicy: '7일 이내 반품 가능',
                images: [],
                taxInvoice: false,
                hasOptions: false,
                options: []
            });
            setImagePreviews([]);
            setActiveTab('basic');
            setOptionGroups([]);

            // 상품 관리 페이지로 이동
            window.location.href = '/product-management';

        } catch (error) {
            console.error('상품 등록 오류:', error);
            alert('상품 등록 중 오류가 발생했습니다: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const tabs = [
        { id: 'basic', label: '기본정보', icon: Package },
        { id: 'price', label: '가격/재고', icon: DollarSign },
        { id: 'options', label: '옵션/추가상품', icon: Layers },
        { id: 'detail', label: '상세정보', icon: AlignLeft },
        { id: 'shipping', label: '배송/정책', icon: Truck },
        { id: 'images', label: '이미지', icon: Upload },
    ];

    return (
        <div style={styles.container}>
            {/* 헤더 */}
            <div style={styles.header}>
                <div style={styles.headerIcon}>
                    <Package size={28} color="#fff" />
                </div>
                <div>
                    <h1 style={styles.title}>상품 등록</h1>
                    <p style={styles.subtitle}>마켓플레이스에 판매할 상품 정보를 입력하세요</p>
                </div>
            </div>

            {/* 탭 네비게이션 */}
            <div style={styles.tabNav}>
                {tabs.map((tab, index) => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                ...styles.tabButton,
                                ...(activeTab === tab.id ? styles.tabButtonActive : {})
                            }}
                        >
                            <Icon size={18} />
                            <span>{tab.label}</span>
                            <span style={styles.tabNumber}>{index + 1}</span>
                        </button>
                    );
                })}
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
                {/* 기본정보 탭 */}
                {activeTab === 'basic' && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>기본 정보</h2>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>상품명 *</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                style={styles.input}
                                placeholder="예: 오스템 TS III SA 임플란트 픽스처"
                                required
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>브랜드</label>
                            <input
                                type="text"
                                value={formData.brand}
                                onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                                style={styles.input}
                                placeholder="예: Osstem, Dentium, Neobiotech"
                            />
                        </div>

                        <div style={styles.row}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>카테고리 *</label>
                                <select
                                    value={formData.category}
                                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value, subCategory: '' }))}
                                    style={styles.select}
                                    required
                                >
                                    {categories.map(cat => (
                                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>세부 카테고리</label>
                                <select
                                    value={formData.subCategory}
                                    onChange={(e) => setFormData(prev => ({ ...prev, subCategory: e.target.value }))}
                                    style={styles.select}
                                    disabled={getSubCategories().length === 0}
                                >
                                    <option value="">선택 안함</option>
                                    {getSubCategories().map(sub => (
                                        <option key={sub.value} value={sub.value}>{sub.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div style={styles.row}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}><Building2 size={16} /> 제조사</label>
                                <input
                                    type="text"
                                    value={formData.manufacturer}
                                    onChange={(e) => setFormData(prev => ({ ...prev, manufacturer: e.target.value }))}
                                    style={styles.input}
                                    placeholder="제조사명"
                                />
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}><Globe size={16} /> 원산지</label>
                                <select
                                    value={formData.origin}
                                    onChange={(e) => setFormData(prev => ({ ...prev, origin: e.target.value }))}
                                    style={styles.select}
                                >
                                    <option value="대한민국">대한민국</option>
                                    <option value="미국">미국</option>
                                    <option value="독일">독일</option>
                                    <option value="일본">일본</option>
                                    <option value="스위스">스위스</option>
                                    <option value="중국">중국</option>
                                    <option value="기타">기타</option>
                                </select>
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}><Award size={16} /> 인증정보</label>
                            <input
                                type="text"
                                value={formData.certification}
                                onChange={(e) => setFormData(prev => ({ ...prev, certification: e.target.value }))}
                                style={styles.input}
                                placeholder="예: FDA, CE, KFDA, ISO 13485"
                            />
                        </div>

                        <div style={styles.navButtons}>
                            <div></div>
                            <button type="button" onClick={() => setActiveTab('price')} style={styles.nextButton}>
                                다음: 가격/재고 →
                            </button>
                        </div>
                    </div>
                )}

                {/* 가격/재고 탭 */}
                {activeTab === 'price' && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>가격 및 재고</h2>

                        <div style={styles.row}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>판매가 (원) *</label>
                                <input
                                    type="number"
                                    value={formData.price}
                                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                                    style={styles.input}
                                    placeholder="0"
                                    min="0"
                                    required
                                />
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>정가 (원) - 할인 표시용</label>
                                <input
                                    type="number"
                                    value={formData.originalPrice}
                                    onChange={(e) => setFormData(prev => ({ ...prev, originalPrice: e.target.value }))}
                                    style={styles.input}
                                    placeholder="0"
                                    min="0"
                                />
                            </div>
                        </div>

                        {formData.originalPrice && formData.price && parseInt(formData.originalPrice) > parseInt(formData.price) && (
                            <div style={styles.discountPreview}>
                                할인율: {Math.round((1 - parseInt(formData.price) / parseInt(formData.originalPrice)) * 100)}% 할인
                            </div>
                        )}

                        <div style={styles.row}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>재고 수량 *</label>
                                <input
                                    type="number"
                                    value={formData.stock}
                                    onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value }))}
                                    style={styles.input}
                                    placeholder="0"
                                    min="0"
                                    required
                                />
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>단위</label>
                                <select
                                    value={formData.unit}
                                    onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                                    style={styles.select}
                                >
                                    {units.map(unit => (
                                        <option key={unit.value} value={unit.value}>{unit.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}><ShoppingCart size={16} /> 최소 주문 수량</label>
                            <input
                                type="number"
                                value={formData.minOrder}
                                onChange={(e) => setFormData(prev => ({ ...prev, minOrder: e.target.value }))}
                                style={styles.input}
                                placeholder="1"
                                min="1"
                            />
                            {formData.stock && formData.minOrder && parseInt(formData.minOrder) > parseInt(formData.stock) && (
                                <p style={{margin:'4px 0 0',fontSize:'13px',color:'#dc2626',fontWeight:'500'}}>
                                    최소 주문 수량은 재고 수량({formData.stock})보다 클 수 없습니다.
                                </p>
                            )}
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>세금계산서 발행</label>
                            <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
                                <label style={{display:'flex',alignItems:'center',gap:'8px',cursor:'pointer',fontSize:'14px'}}>
                                    <input type="checkbox" checked={formData.taxInvoice || false}
                                        onChange={(e) => setFormData({...formData, taxInvoice: e.target.checked})}
                                        style={{width:18,height:18}} />
                                    세금계산서 발행 가능
                                </label>
                            </div>
                        </div>

                        <div style={styles.navButtons}>
                            <button type="button" onClick={() => setActiveTab('basic')} style={styles.prevButton}>
                                ← 이전
                            </button>
                            <button type="button" onClick={() => setActiveTab('options')} style={styles.nextButton}>
                                다음: 옵션/추가상품 →
                            </button>
                        </div>
                    </div>
                )}

                {/* 옵션/추가상품 탭 */}
                {activeTab === 'options' && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>옵션/추가상품 설정</h2>
                        <p style={styles.optionHelp}>
                            상품에 규격, 사이즈, 색상 등 옵션이 있다면 추가하세요. (예: 임플란트 직경/길이)
                        </p>

                        {optionGroups.length === 0 ? (
                            <div style={styles.noOptionBox}>
                                <Layers size={48} color="#cbd5e1" />
                                <p>등록된 옵션이 없습니다</p>
                                <button type="button" onClick={addOptionGroup} style={styles.addOptionBtn}>
                                    <Plus size={18} />
                                    옵션 그룹 추가
                                </button>
                            </div>
                        ) : (
                            <>
                                {optionGroups.map((group, groupIndex) => (
                                    <div key={group.id} style={styles.optionGroupCard}>
                                        <div style={styles.optionGroupHeader}>
                                            <div style={styles.optionGroupNumber}>옵션 {groupIndex + 1}</div>
                                            <button
                                                type="button"
                                                onClick={() => removeOptionGroup(group.id)}
                                                style={styles.deleteGroupBtn}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>

                                        <div style={styles.formGroup}>
                                            <label style={styles.label}>옵션명 *</label>
                                            <input
                                                type="text"
                                                value={group.name}
                                                onChange={(e) => updateOptionGroupName(group.id, e.target.value)}
                                                style={styles.input}
                                                placeholder="예: 직경, 길이, 규격, 색상"
                                            />
                                        </div>

                                        <div style={styles.optionValuesHeader}>
                                            <span style={styles.optionValueLabel}>옵션값</span>
                                            <span style={styles.optionPriceLabel}>추가금액 (원)</span>
                                            <span style={styles.optionStockLabel}>재고</span>
                                            <span style={styles.optionActionLabel}></span>
                                        </div>

                                        {group.values.map((value, valueIndex) => (
                                            <div key={value.id} style={styles.optionValueRow}>
                                                <input
                                                    type="text"
                                                    value={value.value}
                                                    onChange={(e) => updateOptionValue(group.id, value.id, 'value', e.target.value)}
                                                    style={styles.optionValueInput}
                                                    placeholder="예: 4.0mm"
                                                />
                                                <input
                                                    type="number"
                                                    value={value.additionalPrice}
                                                    onChange={(e) => updateOptionValue(group.id, value.id, 'additionalPrice', e.target.value)}
                                                    style={styles.optionPriceInput}
                                                    placeholder="0"
                                                />
                                                <input
                                                    type="number"
                                                    value={value.stock}
                                                    onChange={(e) => updateOptionValue(group.id, value.id, 'stock', e.target.value)}
                                                    style={styles.optionStockInput}
                                                    placeholder="0"
                                                />
                                                {group.values.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeOptionValue(group.id, value.id)}
                                                        style={styles.deleteValueBtn}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}

                                        <button
                                            type="button"
                                            onClick={() => addOptionValue(group.id)}
                                            style={styles.addValueBtn}
                                        >
                                            <Plus size={16} />
                                            옵션값 추가
                                        </button>
                                    </div>
                                ))}

                                <button type="button" onClick={addOptionGroup} style={styles.addOptionGroupBtn}>
                                    <Plus size={18} />
                                    옵션 그룹 추가
                                </button>

                                {/* 옵션 조합 미리보기 */}
                                {generateOptionCombinations().length > 0 && (
                                    <div style={styles.optionPreview}>
                                        <h4 style={styles.previewTitle}>옵션 조합 미리보기 ({generateOptionCombinations().length}개)</h4>
                                        <div style={styles.previewList}>
                                            {generateOptionCombinations().slice(0, 10).map((combo, idx) => (
                                                <div key={idx} style={styles.previewItem}>
                                                    {Object.entries(combo).map(([key, val]) => (
                                                        <span key={key} style={styles.previewBadge}>
                                                            {key}: {val.value}
                                                            {val.additionalPrice > 0 && ` (+${val.additionalPrice.toLocaleString()}원)`}
                                                        </span>
                                                    ))}
                                                </div>
                                            ))}
                                            {generateOptionCombinations().length > 10 && (
                                                <p style={styles.moreText}>... 외 {generateOptionCombinations().length - 10}개</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        <div style={styles.navButtons}>
                            <button type="button" onClick={() => setActiveTab('price')} style={styles.prevButton}>
                                ← 이전
                            </button>
                            <button type="button" onClick={() => setActiveTab('detail')} style={styles.nextButton}>
                                다음: 상세정보 →
                            </button>
                        </div>
                    </div>
                )}

                {/* 상세정보 탭 */}
                {activeTab === 'detail' && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>상세 정보</h2>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>상품 설명</label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                style={styles.textarea}
                                placeholder="상품에 대한 자세한 설명을 입력하세요&#10;&#10;예: 오스템 TS III SA 임플란트는 탁월한 초기 고정력과 골유착률을 자랑하는 프리미엄 임플란트입니다."
                                rows="6"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>주요 특징</label>
                            <textarea
                                value={formData.features}
                                onChange={(e) => setFormData(prev => ({ ...prev, features: e.target.value }))}
                                style={styles.textarea}
                                placeholder="예:&#10;• 우수한 초기 안정성&#10;• SA 표면처리로 빠른 골유착&#10;• 다양한 직경 및 길이 옵션"
                                rows="4"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>제품 사양 / 규격</label>
                            <textarea
                                value={formData.specifications}
                                onChange={(e) => setFormData(prev => ({ ...prev, specifications: e.target.value }))}
                                style={styles.textarea}
                                placeholder="예:&#10;• 직경: 4.0mm, 4.5mm, 5.0mm&#10;• 길이: 8.5mm, 10mm, 11.5mm, 13mm&#10;• 재질: 순수 티타늄 Grade 4"
                                rows="4"
                            />
                        </div>

                        <div style={styles.navButtons}>
                            <button type="button" onClick={() => setActiveTab('price')} style={styles.prevButton}>
                                ← 이전
                            </button>
                            <button type="button" onClick={() => setActiveTab('shipping')} style={styles.nextButton}>
                                다음: 배송/정책 →
                            </button>
                        </div>
                    </div>
                )}

                {/* 배송/정책 탭 */}
                {activeTab === 'shipping' && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>배송 및 반품 정책</h2>

                        <div style={styles.formGroup}>
                            <label style={styles.label}><Truck size={16} /> 배송 정보</label>
                            <select
                                value={formData.shippingInfo}
                                onChange={(e) => setFormData(prev => ({ ...prev, shippingInfo: e.target.value }))}
                                style={styles.select}
                            >
                                <option value="무료배송">무료배송</option>
                                <option value="3,000원">3,000원</option>
                                <option value="5,000원">5,000원</option>
                                <option value="착불">착불</option>
                                <option value="조건부 무료배송">조건부 무료배송 (5만원 이상)</option>
                            </select>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>반품 정책</label>
                            <select
                                value={formData.returnPolicy}
                                onChange={(e) => setFormData(prev => ({ ...prev, returnPolicy: e.target.value }))}
                                style={styles.select}
                            >
                                <option value="7일 이내 반품 가능">7일 이내 반품 가능</option>
                                <option value="14일 이내 반품 가능">14일 이내 반품 가능</option>
                                <option value="30일 이내 반품 가능">30일 이내 반품 가능</option>
                                <option value="개봉 전 반품 가능">개봉 전 반품 가능</option>
                                <option value="반품 불가">반품 불가 (의료기기)</option>
                            </select>
                        </div>

                        <div style={styles.policyInfo}>
                            <h4>안내사항</h4>
                            <ul>
                                <li>의료기기 특성상 개봉 후 반품이 제한될 수 있습니다.</li>
                                <li>유통기한이 있는 제품은 잔여 기간을 명시해주세요.</li>
                                <li>대량 주문 시 별도 배송비가 발생할 수 있습니다.</li>
                            </ul>
                        </div>

                        <div style={styles.navButtons}>
                            <button type="button" onClick={() => setActiveTab('detail')} style={styles.prevButton}>
                                ← 이전
                            </button>
                            <button type="button" onClick={() => setActiveTab('images')} style={styles.nextButton}>
                                다음: 이미지 →
                            </button>
                        </div>
                    </div>
                )}

                {/* 이미지 탭 */}
                {activeTab === 'images' && (
                    <div style={styles.section}>
                        <h2 style={styles.sectionTitle}>상품 이미지 *</h2>
                        <p style={styles.imageHelp}>
                            최소 1개, 최대 5개까지 등록 가능 | 각 이미지 최대 5MB | 첫 번째 이미지가 대표 이미지입니다
                        </p>

                        <input
                            type="file"
                            onChange={handleImageChange}
                            style={styles.fileInput}
                            accept="image/*"
                            multiple
                            id="imageUpload"
                        />
                        <label htmlFor="imageUpload" style={styles.uploadButton}>
                            <Upload size={20} />
                            이미지 선택 ({formData.images.length}/5)
                        </label>

                        {imagePreviews.length > 0 && (
                            <div style={styles.imageGrid}>
                                {imagePreviews.map((preview, index) => (
                                    <div key={index} style={styles.imagePreview}>
                                        <img src={preview} alt={`미리보기 ${index + 1}`} style={styles.previewImage} />
                                        <button
                                            type="button"
                                            onClick={() => removeImage(index)}
                                            style={styles.removeButton}
                                        >
                                            <X size={16} />
                                        </button>
                                        {index === 0 && (
                                            <div style={styles.mainBadge}>대표</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div style={styles.imageGuide}>
                            <h4>이미지 가이드</h4>
                            <ul>
                                <li>제품 전체가 잘 보이는 정면 사진을 대표 이미지로 등록하세요.</li>
                                <li>다양한 각도의 사진을 추가하면 판매에 도움이 됩니다.</li>
                                <li>포장 박스, 구성품 등의 사진도 함께 등록하세요.</li>
                                <li>밝고 선명한 이미지를 사용하세요.</li>
                            </ul>
                        </div>

                        <div style={styles.navButtons}>
                            <button type="button" onClick={() => setActiveTab('shipping')} style={styles.prevButton}>
                                ← 이전
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    ...styles.submitButton,
                                    opacity: loading ? 0.6 : 1,
                                    cursor: loading ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {loading ? '등록 중...' : '상품 등록 신청'}
                            </button>
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '900px',
        margin: '0 auto',
        padding: '24px',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '2px solid #e2e8f0',
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#1e293b',
        margin: 0,
    },
    subtitle: {
        fontSize: '14px',
        color: '#64748b',
        margin: '4px 0 0 0',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '24px',
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    sectionTitle: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#1e293b',
        margin: 0,
        paddingBottom: '12px',
        borderBottom: '1px solid #e2e8f0',
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#334155',
    },
    input: {
        padding: '12px 16px',
        fontSize: '15px',
        border: '2px solid #e2e8f0',
        borderRadius: '10px',
        outline: 'none',
        transition: 'all 0.2s',
    },
    select: {
        padding: '12px 16px',
        fontSize: '15px',
        border: '2px solid #e2e8f0',
        borderRadius: '10px',
        outline: 'none',
        transition: 'all 0.2s',
        backgroundColor: 'white',
        cursor: 'pointer',
    },
    textarea: {
        padding: '12px 16px',
        fontSize: '15px',
        border: '2px solid #e2e8f0',
        borderRadius: '10px',
        outline: 'none',
        transition: 'all 0.2s',
        fontFamily: 'inherit',
        resize: 'vertical',
    },
    imageHelp: {
        fontSize: '13px',
        color: '#64748b',
        margin: 0,
    },
    fileInput: {
        display: 'none',
    },
    uploadButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 24px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#6366f1',
        backgroundColor: '#eef2ff',
        border: '2px dashed #c7d2fe',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        width: 'fit-content',
    },
    imageGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        gap: '16px',
        marginTop: '16px',
    },
    imagePreview: {
        position: 'relative',
        aspectRatio: '1',
        borderRadius: '10px',
        overflow: 'hidden',
        border: '2px solid #e2e8f0',
    },
    previewImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    removeButton: {
        position: 'absolute',
        top: '8px',
        right: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '28px',
        height: '28px',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        color: 'white',
        border: 'none',
        borderRadius: '50%',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    mainBadge: {
        position: 'absolute',
        bottom: '8px',
        left: '8px',
        padding: '4px 8px',
        fontSize: '11px',
        fontWeight: '700',
        color: 'white',
        backgroundColor: '#6366f1',
        borderRadius: '4px',
    },
    submitButton: {
        padding: '16px',
        fontSize: '16px',
        fontWeight: '600',
        color: 'white',
        backgroundColor: '#6366f1',
        border: 'none',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    // 헤더 아이콘
    headerIcon: {
        width: '56px',
        height: '56px',
        borderRadius: '14px',
        backgroundColor: '#6366f1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    // 탭 네비게이션
    tabNav: {
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        overflowX: 'auto',
        paddingBottom: '8px',
    },
    tabButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 20px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        backgroundColor: 'white',
        fontSize: '14px',
        fontWeight: '500',
        color: '#64748b',
        cursor: 'pointer',
        transition: 'all 0.2s',
        whiteSpace: 'nowrap',
        position: 'relative',
    },
    tabButtonActive: {
        backgroundColor: '#6366f1',
        borderColor: '#6366f1',
        color: 'white',
    },
    tabNumber: {
        fontSize: '11px',
        backgroundColor: 'rgba(0,0,0,0.1)',
        padding: '2px 6px',
        borderRadius: '4px',
    },
    // 네비게이션 버튼
    navButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '24px',
        paddingTop: '24px',
        borderTop: '1px solid #e2e8f0',
    },
    prevButton: {
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#64748b',
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        cursor: 'pointer',
    },
    nextButton: {
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: '600',
        color: 'white',
        backgroundColor: '#6366f1',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
    },
    // 할인율 미리보기
    discountPreview: {
        padding: '12px 16px',
        backgroundColor: '#fef2f2',
        color: '#dc2626',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
    },
    // 정책 안내
    policyInfo: {
        padding: '16px',
        backgroundColor: '#f8fafc',
        borderRadius: '10px',
        marginTop: '16px',
    },
    // 이미지 가이드
    imageGuide: {
        padding: '16px',
        backgroundColor: '#f0fdf4',
        borderRadius: '10px',
        marginTop: '20px',
    },
    // 옵션 관련 스타일
    optionHelp: {
        fontSize: '14px',
        color: '#64748b',
        marginBottom: '16px',
    },
    noOptionBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        padding: '48px 24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '2px dashed #e2e8f0',
    },
    addOptionBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: '600',
        color: 'white',
        backgroundColor: '#6366f1',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
    },
    optionGroupCard: {
        padding: '20px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        marginBottom: '16px',
        border: '1px solid #e2e8f0',
    },
    optionGroupHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
    },
    optionGroupNumber: {
        fontSize: '14px',
        fontWeight: '700',
        color: '#6366f1',
    },
    deleteGroupBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
    },
    optionValuesHeader: {
        display: 'grid',
        gridTemplateColumns: '1fr 120px 80px 40px',
        gap: '12px',
        marginTop: '16px',
        marginBottom: '8px',
        paddingBottom: '8px',
        borderBottom: '1px solid #e2e8f0',
    },
    optionValueLabel: { fontSize: '12px', fontWeight: '600', color: '#64748b' },
    optionPriceLabel: { fontSize: '12px', fontWeight: '600', color: '#64748b' },
    optionStockLabel: { fontSize: '12px', fontWeight: '600', color: '#64748b' },
    optionActionLabel: { width: '40px' },
    optionValueRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 120px 80px 40px',
        gap: '12px',
        marginBottom: '8px',
    },
    optionValueInput: {
        padding: '10px 12px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        outline: 'none',
    },
    optionPriceInput: {
        padding: '10px 12px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        outline: 'none',
        textAlign: 'right',
    },
    optionStockInput: {
        padding: '10px 12px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        outline: 'none',
        textAlign: 'right',
    },
    deleteValueBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        backgroundColor: '#f1f5f9',
        color: '#64748b',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
    },
    addValueBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        fontSize: '13px',
        fontWeight: '500',
        color: '#6366f1',
        backgroundColor: 'white',
        border: '1px dashed #c7d2fe',
        borderRadius: '8px',
        cursor: 'pointer',
        marginTop: '8px',
    },
    addOptionGroupBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '14px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#6366f1',
        backgroundColor: 'white',
        border: '2px dashed #c7d2fe',
        borderRadius: '12px',
        cursor: 'pointer',
        width: '100%',
    },
    optionPreview: {
        marginTop: '24px',
        padding: '16px',
        backgroundColor: '#f0fdf4',
        borderRadius: '10px',
        border: '1px solid #bbf7d0',
    },
    previewTitle: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#166534',
        marginBottom: '12px',
    },
    previewList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    previewItem: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
    },
    previewBadge: {
        padding: '4px 10px',
        fontSize: '13px',
        backgroundColor: '#dcfce7',
        color: '#166534',
        borderRadius: '6px',
    },
    moreText: {
        fontSize: '13px',
        color: '#64748b',
        marginTop: '8px',
    },
};

export default ProductRegistration;